import { graphqlPost } from "api/graphqlClient";
import { AxiosResponse } from "axios";
import NotificationService from "components/Notification/Notification.service";
import { createRestError } from "containers/WorkspaceSettings/utils/ErrorNotificationHelper";
import { actions as workspaceSettingsActions } from "containers/WorkspaceSettings/WorkspaceSettings.reducer";
import { Workspace_Configuration } from "graphql/generated/graphql";
import { Action } from "redux";
import { call, CallEffect, put, PutEffect, takeEvery, takeLatest } from "redux-saga/effects";

interface GetSettingsAction extends Action {
    params: {
        query: string;
        variables: {
            limit: number;
            offset: number;
            order: string;
            filter: string;
        };
    };
}

interface SetSettingAction extends Action {
    params: {
        query: string;
        variables: {
            id: string;
            value: any;
        };
    };
    promise: {
        resolve: (value: any) => void;
        reject: (reason: any) => void;
    };
}

interface EmitSettingsAction extends Action {
    type: keyof typeof workspaceSettingsActions;
    settingsList?: Workspace_Configuration[];
    settingSingle?: Workspace_Configuration;
}

type getWorkspaceSettingData = {
    data: {
        workspace_configuration_list: {
            workspace_configuration_count: number;
            workspace_configuration_list: Workspace_Configuration[];
        };
    };
};

type setWorkspaceSettingData = {
    data: { update_workspace_configuration: Workspace_Configuration };
};

export function* fetchWorkspaceSettings({ params }: GetSettingsAction): Generator<
    // step types - types of yielded values inside generator function
    Promise<AxiosResponse<getWorkspaceSettingData>> | PutEffect<EmitSettingsAction>,
    // return type
    void,
    // intermediate argument - return type from yield used inside generator function
    getWorkspaceSettingData
> {
    try {
        const response = yield graphqlPost(params);

        yield put({
            type: workspaceSettingsActions.FETCH_WORKSPACE_SETTINGS_SUCCESSFUL,
            settingsList: response?.data.workspace_configuration_list.workspace_configuration_list,
        });
    } catch (error: any) {
        yield put({
            type: workspaceSettingsActions.FETCH_WORKSPACE_SETTINGS_FAILED,
        });

        createRestError(error);
    }
}

export function* setWorkspaceSetting({ promise, params }: SetSettingAction): Generator<
    // step types - types of yielded values inside generator function
    Promise<AxiosResponse<setWorkspaceSettingData>> | PutEffect<EmitSettingsAction> | CallEffect,
    // return type
    void,
    // intermediate argument - return type from yield used inside generator function
    setWorkspaceSettingData
> {
    try {
        const response = yield graphqlPost(params);

        yield put({
            type: workspaceSettingsActions.SET_WORKSPACE_SETTING_SUCCESS,
            settingSingle: response?.data.update_workspace_configuration,
        });
        yield call(promise.resolve, response?.data.update_workspace_configuration);
        NotificationService.success(`Updated setting successfully`);
    } catch (error: any) {
        yield put({
            type: workspaceSettingsActions.SET_WORKSPACE_SETTING_FAILED,
        });

        createRestError(error);
    }
}

/**
 * Register action to watcher
 */

export default [
    takeLatest(workspaceSettingsActions.START_FETCH_WORKSPACE_SETTINGS, fetchWorkspaceSettings),
    takeEvery(workspaceSettingsActions.SET_WORKSPACE_SETTING, setWorkspaceSetting),
];
